import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Helmet } from "react-helmet";

const HomeCategories = {
    "bedroom": ["bed.png", "wardrobe.png", "pillow.png", "lamp.png", "alarm_clock.png", "bookshelf.png", "curtains.png", "blanket.png", "mirror.png"],
    "bathroom": ["bathtub.png", "toilet.png", "sink.png", "shower.png", "towel.png", "mirror.png", "toothbrush.png", "soap.png", "shampoo.png", "hairdryer.png"],
    "livingroom": ["sofa.png", "television.png", "lamp.png", "bookshelf.png", "clock.png", "curtains.png", "painting.png", "coffee_table.png", "cushion.png"],
    "kitchen": ["stove.png", "refrigerator.png", "microwave.png", "sink.png", "toaster.png", "cabinet.png", "knife.png", "spoon.png", "plate.png"],
    "garden": ["shovel.png", "watering_can.png", "flower_pot.png", "fence.png", "grass.png", "tree.png", "bench.png", "butterfly.png", "birdhouse.png"]
};



function shuffleArray(array: any[]) {
    for (let i = array.length - 1; i > 0; i--) {
        // Generate a random index
        const randomIndex = Math.floor(Math.random() * (i + 1));
        // Swap elements
        [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
    }
    return array;
}

// Question Type
type QuestionType = {
    text: string;
    correctImages: string[];
    allImages: string[];
};



// Dynamic Sizing
const getDynamicSizes = () => {
    const screenWidth = window.innerWidth;
    const imageSize = screenWidth > 1024 ? 90 : screenWidth > 600 ? 100 : 60;
    return { imageSize, minDistance: imageSize * 1.5 };
};

const ChooseHomeItems = () => {
    const [question, setQuestion] = useState<QuestionType | null>(null);
    const [selectedAnswers, setSelectedAnswers] = useState<{ [key: string]: boolean }>({});
    const [sizes, setSizes] = useState(getDynamicSizes());
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [correctImage, setCorrectImage] = useState<any[]>();
    const [selectedImage, setSelectedImage] = useState('');
    const modal = useRef<HTMLIonModalElement>(null);

    // Generate a question
    const generateRandomQuestion = (remainingImages: number): QuestionType => {
        const categories = Object.keys(HomeCategories) as (keyof typeof HomeCategories)[];
        const randomCategory = categories[Math.floor(Math.random() * categories.length)];

        const correctPool = HomeCategories[randomCategory];
        const correctImages = shuffleArray(correctPool).slice(0, 3);
        setCorrectImage(correctImages);

        const otherCategories = categories.filter(cat => cat !== randomCategory);
        const otherImagesPool = otherCategories.flatMap(cat => HomeCategories[cat]);
        const uniqueArr = otherImagesPool.filter((value, index, self) => self.indexOf(value) === index).filter(item => !correctImages.includes(item));
        // Ensure enough incorrect images exist
        const incorrectImages = shuffleArray(uniqueArr).slice(0, remainingImages);
        const allImages = [...correctImages, ...incorrectImages].sort(() => 0.5 - Math.random());

        return {
            text: `Choose ${randomCategory} items`,
            correctImages,
            allImages,
        };
    };

    // Memoized function to generate a new question
    const generateQuestion = useCallback(() => {
        if (!containerRef.current) return;
        //setShowImage(true);
        setTimeout(() => {
            const imageSize = sizes.imageSize;
            const container = containerRef.current?.getBoundingClientRect();
            if (!container) return;

            const columns = Math.floor(container.width / (imageSize + 20));
            const rows = Math.floor(container.height / (imageSize + 20));
            const totalImage = columns * rows;
            const remainingImages = totalImage > 3 ? totalImage - 3 : 0; // Prevent negative values
            setShowImage(false);
            setQuestion(generateRandomQuestion(remainingImages));
        }, 1000);

    }, [sizes]);

    // Generate a question only once on mount
    useEffect(() => {
        generateQuestion();
        document.title = "My Home – Choose the Correct Image Worksheet for UKG | Mama’s Guidance"; // Set page title
    }, []);



    const handleImageClick = (image: string) => {
        setSelectedImage(image);
        setSelectedAnswers(prev => {
            const newState = { ...prev };
            if (image in newState) {
                delete newState[image];
            } else {
                newState[image] = question?.correctImages.includes(image) ?? false;
            }
            return newState;
        });
    };

    // Check answer and generate new question
    useEffect(() => {
        if (!question) return;

        const selectedCorrectCount = Object.values(selectedAnswers).filter(val => val === true).length;
        const totalCorrect = question.correctImages.length;
        const allSelected = Object.keys(selectedAnswers).length === totalCorrect;

        if (selectedCorrectCount === totalCorrect && allSelected) {
            //setShowImage(true);
            setTimeout(() => {
                setSelectedAnswers({});
                setQuestion(null)
                generateQuestion();
                //setShowImage(false);
                setSelectedImage('');

            }, 2000);
        }
    }, [selectedAnswers, question, generateQuestion]);

    return (
        <>
            <Helmet>
                <meta name="description" content="Fun UKG worksheet to choose the correct image related to My Home. Strengthen object recognition and vocabulary through interactive visuals." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/general-awareness/my-home/choose-correct-image" />
            </Helmet>
            {showImage ? (
                <div className="contentPart showMiddle">
                    <img style={{ height: "150px" }} className="img-fluid" src={process.env.REACT_APP_LOADING_LINK} alt="Loading" />
                </div>
            ) : (
                <div className="contentPart additionWord chooseImage match-container">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>My Home – Choose the Correct Image</h1>

                            <p>
                                Let children explore the concept of home with this exciting <strong>Choose the Correct Image</strong> worksheet designed for UKG students. The activity helps little learners identify various household items and rooms through colorful, real-life pictures.
                            </p>

                            <h2>🏠 What Does This Worksheet Include?</h2>
                            <ul>
                                <li>Pictures of common household objects (bed, sofa, fridge, etc.)</li>
                                <li>Rooms of a house (kitchen, bathroom, living room, etc.)</li>
                                <li>Multiple choices with one correct image for each question</li>
                            </ul>

                            <h2>🖼️ Activity Format</h2>
                            <p>
                                Children are shown a word or description (e.g., “This is where we cook food.”) and must <strong>choose the correct image</strong> from three or more options. The visual format builds vocabulary and strengthens memory.
                            </p>

                            <h2>📷 Sample Questions</h2>
                            <ul>
                                <li>Which image shows a <strong>bedroom</strong>? 🛏️</li>
                                <li>Which one is a <strong>sofa</strong>? 🛋️</li>
                                <li>Identify the <strong>kitchen</strong> 🧑‍🍳</li>
                                <li>Select the image of a <strong>washing machine</strong> 🧺</li>
                            </ul>

                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Sense Organs</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/general-awareness/my-home"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>My Home</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/general-awareness/my-school"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>My School</IonLabel>
                                </IonItem>
                            </div>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Visual recognition of home objects and places</li>
                                <li>Strengthening word-picture association</li>
                                <li>Improved vocabulary and memory skills</li>
                                <li>Increased observation and attention to detail</li>
                            </ul>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>My Home – Choose the Correct Image</h1>

                        <p>
                            Let children explore the concept of home with this exciting <strong>Choose the Correct Image</strong> worksheet designed for UKG students. The activity helps little learners identify various household items and rooms through colorful, real-life pictures.
                        </p>

                        <h2>🏠 What Does This Worksheet Include?</h2>
                        <ul>
                            <li>Pictures of common household objects (bed, sofa, fridge, etc.)</li>
                            <li>Rooms of a house (kitchen, bathroom, living room, etc.)</li>
                            <li>Multiple choices with one correct image for each question</li>
                        </ul>

                        <h2>🖼️ Activity Format</h2>
                        <p>
                            Children are shown a word or description (e.g., “This is where we cook food.”) and must <strong>choose the correct image</strong> from three or more options. The visual format builds vocabulary and strengthens memory.
                        </p>

                        <h2>📷 Sample Questions</h2>
                        <ul>
                            <li>Which image shows a <strong>bedroom</strong>? 🛏️</li>
                            <li>Which one is a <strong>sofa</strong>? 🛋️</li>
                            <li>Identify the <strong>kitchen</strong> 🧑‍🍳</li>
                            <li>Select the image of a <strong>washing machine</strong> 🧺</li>
                        </ul>

                        <h2>🔗 Related Topics</h2>
                        <div>
                            <IonItem routerLink="/ukg/general-awareness/sense-organ"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>Sense Organs</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/general-awareness/my-home"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>My Home</IonLabel>
                            </IonItem>
                            <IonItem routerLink="/ukg/general-awareness/my-school"
                                routerDirection="none"
                                lines="none"
                                detail={true}>
                                <IonLabel>My School</IonLabel>
                            </IonItem>
                        </div>

                        <h2>🎯 Learning Objectives</h2>
                        <ul>
                            <li>Visual recognition of home objects and places</li>
                            <li>Strengthening word-picture association</li>
                            <li>Improved vocabulary and memory skills</li>
                            <li>Increased observation and attention to detail</li>
                        </ul>
                    </article>
                    <h6 style={{ textAlign: "center" }}>{question?.text}</h6>
                    <div ref={containerRef} className="chooseImageContainer" style={{ position: "relative", width: "100%", height: "100%", justifyContent: "center", alignItems: "center", overflow: "hidden", display: "grid" }}>
                        {question?.allImages.map((img, index) => (
                            <div key={img + index} >
                                <img src={`${process.env.REACT_APP_API_URL}${img}`} alt="option" onClick={() => handleImageClick(img)} style={{
                                    width: `${sizes.imageSize}px`,
                                    height: `${sizes.imageSize}px`,
                                    cursor: "pointer",
                                    border: `5px solid ${img in selectedAnswers ? (selectedAnswers[img] === true ? "green" : "red") : "transparent"}`,
                                    transition: "transform 0.2s ease-in-out",
                                }} />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
};

export default ChooseHomeItems;
